import React, { useContext, useEffect, useRef } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Typography,
} from 'antd';

import RulesContext from '@totem/components/rulesEngine/report/rulesContext';
import {
  IBHierarchyReference,
  ObjectReference,
  Rule,
  RuleScopeType,
} from '@totem/components/rulesEngine/types';
import OrgHierarchySelector from '@totem/components/ui_components/widgets/hierarchySelector/orgHierarchySelector';

const EMPTY_REF: ObjectReference = { id: '', name: '' };
const EMPTY_SCOPE: IBHierarchyReference = {
  organization: EMPTY_REF,
  region: EMPTY_REF,
  building: EMPTY_REF,
  controlSystem: EMPTY_REF,
};

const DEFAULT_GROUP_JSON = JSON.stringify(
  { id: '', operator: 'AND', conditions: [], groups: [] },
  null,
  2,
);

const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const SCOPE_TYPE_OPTIONS = [
  { value: 'System', label: 'System (runs against everything)' },
  { value: 'User', label: 'User (scoped to specific org/building)' },
];

const validateJson = (label: string) => ({
  validator: (_rule: unknown, value: string) => {
    if (!value || !value.trim()) {
      return Promise.resolve();
    }
    try {
      JSON.parse(value);
      return Promise.resolve();
    } catch {
      return Promise.reject(new Error(`${label} must be valid JSON`));
    }
  },
});

const RuleDialog = () => {
  const { editingRule, modalOpen, saving, onSave, onModalClose } =
    useContext(RulesContext);
  const [form] = Form.useForm();
  const scope = useRef<IBHierarchyReference>(EMPTY_SCOPE);

  const scopeType: RuleScopeType = Form.useWatch('scopeType', form);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }
    if (editingRule) {
      scope.current = editingRule.scope ?? EMPTY_SCOPE;

      form.setFieldsValue({
        name: editingRule.name,
        domain: editingRule.domain,
        trigger: editingRule.trigger ?? [],
        status: editingRule.status || 'Active',
        sequence: editingRule.sequence ?? 100,
        scopeType: editingRule.scopeType || 'System',
        tags: editingRule.tags ?? [],
        group: JSON.stringify(
          editingRule.group ?? JSON.parse(DEFAULT_GROUP_JSON),
          null,
          2,
        ),
        actionsList: (editingRule.actions ?? []).map((action) => ({
          id: action.id,
          type: action.type,
          data: action.data,
          claimKeys: action.claimKeys ?? [],
          cancelsKeys: action.cancelsKeys ?? [],
        })),
      });
    } else {
      scope.current = EMPTY_SCOPE;

      form.resetFields();

      form.setFieldsValue({
        status: 'Active',
        sequence: 100,
        scopeType: 'System',
        group: DEFAULT_GROUP_JSON,
        actionsList: [],
      });
    }
  }, [modalOpen, editingRule, form]);

  const handleScopeChange = (
    organization: ObjectReference,
    region: ObjectReference,
    building: ObjectReference,
    controlSystem: ObjectReference,
  ) => {
    scope.current = { organization, region, building, controlSystem };
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      const rule: Partial<Rule> = {
        ...(editingRule?.id ? { id: editingRule.id } : {}),
        name: values.name,
        domain: values.domain,
        trigger: values.trigger ?? [],
        status: values.status,
        sequence: values.sequence,
        scopeType: values.scopeType,
        scope: values.scopeType === 'User' ? scope.current : EMPTY_SCOPE,
        tags: values.tags ?? [],
        group: JSON.parse(values.group),
        actions: (values.actionsList ?? []).map((action) => ({
          id: action.id ?? '',
          type: action.type,
          data: action.data ?? '',
          claimKeys: action.claimKeys ?? [],
          cancelsKeys: action.cancelsKeys ?? [],
        })),
        ...(editingRule?.createdBy ? { createdBy: editingRule.createdBy } : {}),
      };

      onSave(rule);
    });
  };

  return (
    <Modal
      title={editingRule ? 'Edit Rule' : 'New Rule'}
      open={modalOpen}
      onOk={handleOk}
      onCancel={onModalClose}
      okText={editingRule ? 'Save' : 'Create'}
      okButtonProps={{ disabled: saving }}
      width={760}
      destroyOnClose
    >
      <Spin spinning={saving}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={11}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Name is required' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="domain"
                label="Domain"
                rules={[{ required: true, message: 'Domain is required' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true }]}
              >
                <Select options={STATUS_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="trigger"
            label="Trigger"
            rules={[
              { required: true, message: 'At least one trigger is required' },
            ]}
          >
            <Select mode="tags" placeholder="e.g. CREATED, UPDATE" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="sequence"
                label="Sequence"
                rules={[{ required: true, message: 'Sequence is required' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={18}>
              <Form.Item
                name="scopeType"
                label="Scope Type"
                rules={[{ required: true }]}
              >
                <Select options={SCOPE_TYPE_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          {scopeType === 'User' && (
            <Form.Item label="Scope">
              <OrgHierarchySelector
                onChange={handleScopeChange}
                showOrganization
                showRegions
                showBuildings
                showControlSystems
                allowUnassignedOrganization
                allowUnassignedBuilding
                defaultRegionId={editingRule?.scope?.region?.id}
                defaultBuildingId={editingRule?.scope?.building?.id}
                defaultControlSystemId={editingRule?.scope?.controlSystem?.id}
              />
            </Form.Item>
          )}

          <Form.Item name="tags" label="Tags">
            <Select mode="tags" placeholder="Add tags for categorization" />
          </Form.Item>

          <Form.Item
            name="group"
            label="Condition Group (JSON)"
            rules={[validateJson('Condition group')]}
          >
            <Input.TextArea rows={6} style={{ fontFamily: 'monospace' }} />
          </Form.Item>

          <Typography.Title level={5}>Actions</Typography.Title>
          <Form.List name="actionsList">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Card
                    key={field.key}
                    size="small"
                    style={{ marginBottom: 12 }}
                    extra={
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    }
                  >
                    <Form.Item name={[field.name, 'id']} hidden>
                      <Input />
                    </Form.Item>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name={[field.name, 'type']}
                          label="Type"
                          rules={[
                            { required: true, message: 'Type is required' },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name={[field.name, 'data']}
                          label="Data (JSON)"
                          rules={[validateJson('Action data')]}
                        >
                          <Input.TextArea
                            rows={2}
                            style={{ fontFamily: 'monospace' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name={[field.name, 'claimKeys']}
                          label="Claim Keys"
                        >
                          <Select
                            mode="tags"
                            placeholder="Keys this action claims"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name={[field.name, 'cancelsKeys']}
                          label="Cancels Keys"
                        >
                          <Select
                            mode="tags"
                            placeholder="Keys this action cancels for later rules"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={() =>
                    add({
                      id: '',
                      type: '',
                      data: '',
                      claimKeys: [],
                      cancelsKeys: [],
                    })
                  }
                >
                  Add Action
                </Button>
              </>
            )}
          </Form.List>

          {editingRule?.createdBy?.name && (
            <Typography.Text type="secondary">
              Created by {editingRule.createdBy.name}
            </Typography.Text>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default RuleDialog;
